<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPropertySectionNav from './SalesPropertySectionNav.vue';

const props = defineProps({
  transaction: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  progressLabel: { type: String, default: '' },
  reviewCount: { type: Number, default: 0 },
  reviewMode: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  sectionComponent: { type: [Object, Function], required: true },
  sectionBindings: { type: Object, default: () => ({}) },
  sectionListeners: { type: Object, default: () => ({}) },
  activeSection: { type: String, required: true },
  sectionChecks: { type: Object, default: () => ({}) },
  sectionKeys: { type: Array, required: true },
  currentIndex: { type: Number, required: true },
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  property: { type: Object, default: () => ({}) },
  isComplete: { type: Function, required: true },
  referenceLayout: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select',
  'previous',
  'next',
  'showCorrections',
  'completeReview',
  'update',
  'updateNested',
]);
const { t } = useI18n();

const forwardUpdate = (...args) => emit('update', ...args);
const forwardNestedUpdate = (...args) => emit('updateNested', ...args);
const normalNextLabel = () => {
  if (props.currentIndex === props.sectionKeys.length - 1) {
    return props.referenceLayout
      ? t('SALES_PROPERTY.FOOTER.BLOCK_COMPLETE')
      : t('SALES_PROPERTY.FOOTER.COMPLETE');
  }

  return props.referenceLayout
    ? t('SALES_PROPERTY.FOOTER.NEXT')
    : t('SALES_PROPERTY.ACTIONS.NEXT');
};
</script>

<template>
  <main
    class="grid w-full"
    :class="
      referenceLayout
        ? 'min-h-[calc(100vh-66px)] bg-lais-bg-soft min-[701px]:grid-cols-[20vw_minmax(0,80vw)]'
        : 'mx-auto max-w-[1500px] gap-5 px-4 pb-8 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:px-10'
    "
  >
    <aside
      :class="
        referenceLayout
          ? 'border-r border-lais-border-soft bg-white py-2 max-[700px]:hidden'
          : ''
      "
    >
      <SalesPropertySectionNav
        :active-section="activeSection"
        :is-complete="isComplete"
        :reference-layout="referenceLayout"
        :review-mode="reviewMode"
        :review-count="reviewCount"
        :section-checks="sectionChecks"
        :section-keys="sectionKeys"
        @select="emit('select', $event)"
      />
    </aside>

    <section
      class="grid min-w-0 gap-4"
      :class="
        referenceLayout
          ? 'relative bg-lais-bg-soft px-4 pb-6 pt-[50px] min-[701px]:pb-[96px] min-[701px]:pl-[50px] min-[701px]:pr-0 max-[700px]:px-0'
          : ''
      "
      :aria-label="t('SALES_PROPERTY.ARIA.FORM')"
    >
      <LaisCard
        v-if="transaction && !referenceLayout"
        :title="transaction.title"
        prefix-icon="i-lucide-house"
      >
        <div class="flex flex-wrap items-center justify-between gap-3 text-sm">
          <span class="text-lais-text-muted">
            {{ property.code || t('SALES_PROPERTY.COMMON.NO_DATA') }}
          </span>
          <span
            class="rounded-full bg-lais-primary-50 px-3 py-1 font-bold text-lais-primary"
          >
            {{ progressLabel }}
          </span>
        </div>
      </LaisCard>

      <div
        v-if="loading"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </div>
      <div
        v-if="transaction && !loading"
        class="grid gap-4"
        :class="referenceLayout ? 'w-full max-w-[896px]' : ''"
      >
        <component
          :is="sectionComponent"
          :profile="profile"
          :errors="errors"
          :property="property"
          v-bind="sectionBindings"
          v-on="sectionListeners"
          @update="forwardUpdate"
          @update-nested="forwardNestedUpdate"
        >
          <template v-if="!reviewMode" #footer>
            <footer
              class="mt-[10px] flex min-h-[60px] items-center justify-between gap-3"
              data-test="sales-property-block-footer"
            >
              <div>
                <LaisPrimaryButton
                  v-if="currentIndex > 0"
                  data-test="sales-property-block-previous"
                  variant="link"
                  size="lg"
                  icon="i-lucide-arrow-left"
                  class="!h-[60px] !px-6 !text-base !font-bold !text-[#464F53]"
                  @click="emit('previous')"
                >
                  {{
                    referenceLayout
                      ? t('SALES_PROPERTY.FOOTER.PREVIOUS')
                      : t('SALES_PROPERTY.ACTIONS.PREVIOUS')
                  }}
                </LaisPrimaryButton>
              </div>
              <LaisPrimaryButton
                data-test="sales-property-block-next"
                size="lg"
                icon="i-lucide-arrow-right"
                class="!h-[60px] flex-row-reverse !px-6 !text-base !font-bold"
                :class="
                  referenceLayout ? '!bg-[#14B8AA] hover:!bg-[#0D9488]' : ''
                "
                @click="emit('next')"
              >
                {{ normalNextLabel() }}
              </LaisPrimaryButton>
            </footer>
          </template>
        </component>
        <div
          v-if="reviewMode"
          class="flex flex-wrap items-center justify-between gap-3"
        >
          <LaisPrimaryButton
            variant="ghost"
            :disabled="saving"
            @click="emit('showCorrections')"
          >
            {{
              t('SALES_PROPERTY.ACTIONS.SHOW_CORRECTIONS', {
                count: reviewCount,
              })
            }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-check"
            :is-loading="saving"
            @click="emit('completeReview')"
          >
            {{ t('SALES_PROPERTY.ACTIONS.COMPLETE_CORRECTION') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </section>
  </main>
</template>
